import type { FieldDefinition } from "./templates";

/**
 * Universal RPG Field Schemas directly extracted and adapted from Craft RPGs (craft_file_types.json).
 * Designed for rich entity modeling, bilingual persistence (English data keys), and seamless
 * integration with the Virtual File System (VFS).
 */

export const CRAFT_CHARACTER_FIELDS: FieldDefinition[] = [
  { id: "name", label: "Name", type: "text", required: true },
  { id: "level", label: "Level", type: "number", required: true, defaultValue: 1 },
  { id: "class", label: "Class", type: "text" },
  { id: "species", label: "Species / Race", type: "text" },
  { id: "age", label: "Age", type: "number" },
  { id: "gender", label: "Gender", type: "text" },
  { id: "alignment", label: "Alignment", type: "text" },
  { id: "backstory", label: "Backstory", type: "long_text" },
  { id: "goals", label: "Goals", type: "list", item: { id: "goal", label: "Goal", type: "text" } },
  { id: "fears", label: "Fears", type: "list", item: { id: "fear", label: "Fear", type: "text" } },
  { id: "flaws", label: "Flaws", type: "list", item: { id: "flaw", label: "Flaw", type: "text" } },
  { id: "desire", label: "Desire", type: "long_text" },
  { id: "notes", label: "Notes", type: "long_text" },
  { id: "gmNotes", label: "GM Notes", type: "long_text" },
  { id: "tags", label: "Tags", type: "list", item: { id: "tag", label: "Tag", type: "text" } },
  { id: "gold", label: "Gold", type: "number", defaultValue: 0 },
  { id: "speed", label: "Speed", type: "number", defaultValue: 30 },
  { id: "equipment", label: "Equipment", type: "list", item: { id: "itemRef", label: "Item", type: "reference" } },
];

export const CRAFT_NPC_FIELDS: FieldDefinition[] = [
  { id: "name", label: "Name", type: "text", required: true },
  {
    id: "role",
    label: "Role",
    type: "enum",
    options: ["Major NPC", "Minor NPC", "Ally", "Rival", "Enemy", "Neutral", "Companion", "Faction Leader", "Other"],
    defaultValue: "Neutral",
  },
  { id: "species", label: "Species", type: "text" },
  { id: "tone", label: "Tone / Attitude", type: "text" },
  { id: "description", label: "Description", type: "long_text" },
  { id: "goals", label: "Goals", type: "list", item: { id: "goal", label: "Goal", type: "text" } },
  { id: "location", label: "Current Location", type: "text" },
  { id: "notes", label: "Notes", type: "long_text" },
  { id: "gmNotes", label: "GM Notes", type: "long_text" },
  { id: "tags", label: "Tags", type: "list", item: { id: "tag", label: "Tag", type: "text" } },
];

export const CRAFT_LOCATION_FIELDS: FieldDefinition[] = [
  { id: "name", label: "Name", type: "text", required: true },
  { id: "type", label: "Location Type", type: "text" },
  { id: "description", label: "Description", type: "long_text" },
  { id: "dangerLevel", label: "Danger Level", type: "number" },
  { id: "notes", label: "Notes", type: "long_text" },
  { id: "gmNotes", label: "GM Notes", type: "long_text" },
  { id: "tags", label: "Tags", type: "list", item: { id: "tag", label: "Tag", type: "text" } },
  { id: "factions", label: "Factions Present", type: "list", item: { id: "factionRef", label: "Faction", type: "reference" } },
  {
    id: "existence",
    label: "Existence State",
    type: "object",
    properties: [
      {
        id: "status",
        label: "Status",
        type: "enum",
        options: ["Active", "Dormant", "Gone", "Unknown"],
        defaultValue: "Active",
      },
      { id: "doingNow", label: "Doing Now", type: "text" },
      { id: "endedHow", label: "Ended How", type: "text" },
    ],
  },
];

export const CRAFT_MONSTER_FIELDS: FieldDefinition[] = [
  { id: "name", label: "Name", type: "text", required: true },
  { id: "cr", label: "Challenge Rating / Level", type: "number", defaultValue: 1 },
  { id: "hp", label: "Hit Points", type: "number", defaultValue: 10 },
  { id: "ac", label: "Armor Class", type: "number", defaultValue: 10 },
  { id: "speed", label: "Speed", type: "number", defaultValue: 30 },
  { id: "type", label: "Creature Type", type: "text" },
  { id: "alignment", label: "Alignment", type: "text" },
  { id: "description", label: "Description", type: "long_text" },
  { id: "abilities", label: "Abilities / Features", type: "list", item: { id: "ability", label: "Ability", type: "text" } },
  { id: "actions", label: "Actions / Attacks", type: "list", item: { id: "action", label: "Action", type: "text" } },
  { id: "tags", label: "Tags", type: "list", item: { id: "tag", label: "Tag", type: "text" } },
  { id: "notes", label: "Notes", type: "long_text" },
];

export const CRAFT_ITEM_FIELDS: FieldDefinition[] = [
  { id: "name", label: "Name", type: "text", required: true },
  {
    id: "category",
    label: "Category",
    type: "enum",
    options: ["Weapon", "Armor", "Shield", "Consumable", "Tool", "Adventuring Gear", "Magic Item"],
    defaultValue: "Adventuring Gear",
  },
  {
    id: "rarity",
    label: "Rarity",
    type: "enum",
    options: ["Common", "Uncommon", "Rare", "Very Rare", "Legendary", "Artifact"],
    defaultValue: "Common",
  },
  { id: "cost", label: "Cost", type: "text" },
  { id: "weight", label: "Weight", type: "number" },
  { id: "description", label: "Description", type: "long_text" },
  { id: "properties", label: "Properties", type: "list", item: { id: "prop", label: "Property", type: "text" } },
  { id: "damage", label: "Damage / Effect", type: "text" },
  { id: "tags", label: "Tags", type: "list", item: { id: "tag", label: "Tag", type: "text" } },
];

export const CRAFT_FACTION_FIELDS: FieldDefinition[] = [
  { id: "name", label: "Name", type: "text", required: true },
  { id: "description", label: "Description", type: "long_text" },
  { id: "goals", label: "Goals", type: "list", item: { id: "goal", label: "Goal", type: "text" } },
  {
    id: "attitude",
    label: "Player Attitude",
    type: "enum",
    options: ["Friendly", "Neutral", "Hostile", "Secretive", "Allied"],
    defaultValue: "Neutral",
  },
  {
    id: "influence",
    label: "Influence",
    type: "enum",
    options: ["Local", "Regional", "Continental", "Planar"],
    defaultValue: "Regional",
  },
  { id: "territory", label: "Territory", type: "text" },
  { id: "leader", label: "Leader", type: "text" },
  { id: "tags", label: "Tags", type: "list", item: { id: "tag", label: "Tag", type: "text" } },
];

export const CRAFT_QUEST_FIELDS: FieldDefinition[] = [
  { id: "title", label: "Title", type: "text", required: true },
  { id: "summary", label: "Summary", type: "long_text" },
  { id: "objectives", label: "Objectives", type: "list", item: { id: "objective", label: "Objective", type: "text" } },
  {
    id: "status",
    label: "Status",
    type: "enum",
    options: ["Active", "Completed", "Failed", "Abandoned"],
    defaultValue: "Active",
  },
  { id: "rewards", label: "Rewards", type: "text" },
  { id: "giver", label: "Quest Giver", type: "text" },
  { id: "tags", label: "Tags", type: "list", item: { id: "tag", label: "Tag", type: "text" } },
];

export const CRAFT_LORE_FIELDS: FieldDefinition[] = [
  { id: "title", label: "Title", type: "text", required: true },
  { id: "category", label: "Category", type: "text" },
  { id: "summary", label: "Summary", type: "long_text" },
  { id: "content", label: "Content", type: "long_text" },
  { id: "tags", label: "Tags", type: "list", item: { id: "tag", label: "Tag", type: "text" } },
];

/**
 * Complete map of canonical Craft core schemas indexed by designation.
 */
export const CRAFT_CORE_FIELDS: Record<string, FieldDefinition[]> = {
  character: CRAFT_CHARACTER_FIELDS,
  npc: CRAFT_NPC_FIELDS,
  location: CRAFT_LOCATION_FIELDS,
  monster: CRAFT_MONSTER_FIELDS,
  item: CRAFT_ITEM_FIELDS,
  weapon: CRAFT_ITEM_FIELDS,
  armor: CRAFT_ITEM_FIELDS,
  shield: CRAFT_ITEM_FIELDS,
  consumable: CRAFT_ITEM_FIELDS,
  gear: CRAFT_ITEM_FIELDS,
  faction: CRAFT_FACTION_FIELDS,
  quest: CRAFT_QUEST_FIELDS,
  lore: CRAFT_LORE_FIELDS,
  rule: CRAFT_LORE_FIELDS,
};
